/* AT600 configurator v3: sticky price bar */
if (!customElements.get('at600-configurator')) {
  customElements.define('at600-configurator', class AquaTroll600Configurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.selects = [...this.querySelectorAll('[data-at600-select]')];
      this.submitButton = this.querySelector('[data-at600-submit]');
      this.totalElement = this.querySelector('[data-at600-total]');
      this.basePriceElement = this.querySelector('[data-at600-base-price]');
      this.componentsElement = this.querySelector('[data-at600-components]');
      this.errorElement = this.querySelector('[data-at600-error]');
      this.errorMessage = this.errorElement.querySelector('.product-form__error-message');
      this.stickyBar = this.querySelector('[data-at600-bar]');
      this.stickyProgress = this.querySelector('[data-at600-bar-progress]');
      this.stickyAddons = this.querySelector('[data-at600-bar-addons]');
      this.stickyTotal = this.querySelector('[data-at600-bar-total]');
      this.stickySubmit = this.querySelector('[data-at600-bar-submit]');

      const variantData = JSON.parse(this.querySelector('[data-at600-variants]').textContent);
      this.variantsBySku = new Map(variantData.map((variant) => [variant.sku, variant]));
      this.baseVariant = this.variantsBySku.get('0096400');

      this.selects.forEach((select) => select.addEventListener('change', () => this.updateSummary()));
      this.submitButton.addEventListener('click', () => this.addConfiguration());
      this.stickySubmit?.addEventListener('click', () => this.addConfiguration());

      if (!this.baseVariant) {
        this.showError('The AT-800 base variant is not available. Please contact LakeTech.');
        this.submitButton.disabled = true;
        if (this.stickySubmit) this.stickySubmit.disabled = true;
        return;
      }

      this.basePriceElement.textContent = this.formatMoney(this.baseVariant.price);
      this.enhanceSelects();
      this.updateSummary();
      this.setupStickyBar();
    }

    // Replace the native select popups (unstylable OS grey menus) with a
    // branded listbox. The native selects stay in the DOM as the source of
    // truth for values, validation, and change events.
    enhanceSelects() {
      this.classList.add('lt-at600--enhanced');
      this.openDropdown = null;

      this.selects.forEach((select) => this.buildCustomSelect(select));

      document.addEventListener('click', (event) => {
        if (this.openDropdown && !this.openDropdown.wrapper.contains(event.target)) this.closeMenu();
      });
    }

    buildCustomSelect(select) {
      const wrapper = select.closest('.select');
      if (!wrapper) return;
      wrapper.classList.add('lt-dd');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');

      const label = this.querySelector(`label[for="${select.id}"]`);
      if (label && !label.id) label.id = `${select.id}-label`;

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.id = `${select.id}-trigger`;
      trigger.className = 'lt-dd__trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      if (label) trigger.setAttribute('aria-labelledby', `${label.id} ${trigger.id}`);
      trigger.innerHTML = `
        <span class="lt-dd__value"></span>
        <svg class="lt-dd__chevron" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
          <path d="M1 1.5 6 6.5 11 1.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;

      const menu = document.createElement('div');
      menu.className = 'lt-dd__menu';
      menu.id = `${select.id}-menu`;
      menu.setAttribute('role', 'listbox');
      if (label) menu.setAttribute('aria-labelledby', label.id);
      trigger.setAttribute('aria-controls', menu.id);

      const items = [];
      [...select.options].forEach((option) => {
        if (option.value === '') return;
        const item = document.createElement('div');
        item.className = 'lt-dd__option';
        item.id = `${select.id}-opt-${items.length}`;
        item.setAttribute('role', 'option');
        item.dataset.value = option.value;

        const name = document.createElement('span');
        name.className = 'lt-dd__option-name';
        name.textContent = option.value;
        item.append(name);

        const sku = option.dataset.componentSku;
        const variant = sku ? this.variantsBySku.get(sku) : null;
        if (variant && Number(variant.price) > 0) {
          const price = document.createElement('span');
          price.className = 'lt-dd__option-price';
          price.textContent = `+${this.formatMoney(variant.price)}`;
          item.append(price);
        }

        item.addEventListener('click', () => this.chooseValue(select, option.value));
        item.addEventListener('mouseenter', () => this.setActive(items.indexOf(item)));
        menu.append(item);
        items.push(item);
      });

      // Keep focus on the trigger when clicking menu items, so focusout
      // does not close the menu before the click lands.
      menu.addEventListener('mousedown', (event) => event.preventDefault());

      trigger.addEventListener('click', () => {
        if (this.openDropdown?.select === select) this.closeMenu();
        else this.openMenu(select);
      });
      trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event, select));
      wrapper.addEventListener('focusout', (event) => {
        if (this.openDropdown?.select === select && !wrapper.contains(event.relatedTarget)) this.closeMenu();
      });
      if (label) label.addEventListener('click', (event) => { event.preventDefault(); trigger.focus(); });

      wrapper.append(trigger, menu);
      select.customDropdown = { wrapper, trigger, menu, items, activeIndex: -1 };
      select.addEventListener('change', () => this.syncTrigger(select));
      this.syncTrigger(select);
    }

    syncTrigger(select) {
      const dropdown = select.customDropdown;
      if (!dropdown) return;
      const valueElement = dropdown.trigger.querySelector('.lt-dd__value');
      const placeholder = select.options[0]?.value === '' ? select.options[0].text : 'Select an option';
      valueElement.textContent = select.value || placeholder;
      dropdown.trigger.classList.toggle('lt-dd__trigger--placeholder', !select.value);
      dropdown.items.forEach((item) => {
        item.setAttribute('aria-selected', String(item.dataset.value === select.value));
      });
    }

    openMenu(select) {
      if (this.openDropdown && this.openDropdown.select !== select) this.closeMenu();
      const dropdown = select.customDropdown;
      this.openDropdown = { ...dropdown, select, wrapper: dropdown.wrapper };
      dropdown.menu.classList.add('is-open');
      dropdown.menu.classList.remove('lt-dd__menu--align-right');
      const rect = dropdown.menu.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      if (viewportWidth && rect.right > viewportWidth - 12) {
        dropdown.menu.classList.add('lt-dd__menu--align-right');
      }
      dropdown.trigger.setAttribute('aria-expanded', 'true');
      const selected = dropdown.items.findIndex((item) => item.dataset.value === select.value);
      this.setActive(selected >= 0 ? selected : 0);
    }

    closeMenu() {
      if (!this.openDropdown) return;
      const { select } = this.openDropdown;
      const dropdown = select.customDropdown;
      dropdown.menu.classList.remove('is-open');
      dropdown.trigger.setAttribute('aria-expanded', 'false');
      dropdown.trigger.removeAttribute('aria-activedescendant');
      dropdown.items.forEach((item) => item.classList.remove('is-active'));
      this.openDropdown = null;
    }

    setActive(index) {
      if (!this.openDropdown) return;
      const dropdown = this.openDropdown.select.customDropdown;
      dropdown.activeIndex = index;
      dropdown.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
      const active = dropdown.items[index];
      if (active) {
        dropdown.trigger.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      }
    }

    chooseValue(select, value) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      this.closeMenu();
      select.customDropdown.trigger.focus();
    }

    onTriggerKeydown(event, select) {
      const dropdown = select.customDropdown;
      const open = this.openDropdown?.select === select;

      if (!open) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          this.openMenu(select);
        }
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.setActive(Math.min(dropdown.activeIndex + 1, dropdown.items.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.setActive(Math.max(dropdown.activeIndex - 1, 0));
          break;
        case 'Home':
          event.preventDefault();
          this.setActive(0);
          break;
        case 'End':
          event.preventDefault();
          this.setActive(dropdown.items.length - 1);
          break;
        case 'Enter':
        case ' ': {
          event.preventDefault();
          const active = dropdown.items[dropdown.activeIndex];
          if (active) this.chooseValue(select, active.dataset.value);
          break;
        }
        case 'Escape':
          event.preventDefault();
          this.closeMenu();
          break;
        case 'Tab':
          this.closeMenu();
          break;
      }
    }

    setupStickyBar() {
      if (!this.stickyBar) return;

      document.querySelectorAll('[data-lt-mobile-purchase-bar]').forEach((bar) => {
        bar.style.display = 'none';
      });

      this.stickyBar.hidden = false;
      this.stickyAnchor = this.querySelector('.lt-at600__summary');

      const update = () => this.updateStickyVisibility();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    }

    // Show the bar only while the in-panel summary is off screen, so the
    // price is always in view without ever duplicating it.
    updateStickyVisibility() {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      let show = true;

      if (this.stickyAnchor && viewportHeight) {
        const rect = this.stickyAnchor.getBoundingClientRect();
        const summaryInView = rect.top < viewportHeight - 80 && rect.bottom > 0;
        show = !summaryInView;
      }

      this.stickyBar.classList.toggle('lt-at600-bar--visible', show);
    }

    getSelection(select) {
      const option = select.options[select.selectedIndex];
      const sku = option?.dataset.componentSku || '';
      return {
        property: select.dataset.property,
        value: select.value,
        sku,
        variant: sku ? this.variantsBySku.get(sku) : null,
      };
    }

    updateSummary() {
      this.clearError();
      const selections = this.selects.map((select) => this.getSelection(select));
      const selectedComponents = selections.filter((selection) => selection.sku);
      const missingVariant = selectedComponents.find((selection) => !selection.variant);
      const unavailableVariant = selectedComponents.find((selection) => selection.variant && !selection.variant.available);
      let total = Number(this.baseVariant.price);

      selectedComponents.forEach((selection) => {
        if (selection.variant) total += Number(selection.variant.price);
      });

      this.totalElement.textContent = this.formatMoney(total);
      this.componentsElement.replaceChildren();

      if (!selectedComponents.length) {
        const prompt = document.createElement('p');
        prompt.textContent = 'Choose all options to see the complete configuration.';
        this.componentsElement.append(prompt);
      } else {
        selectedComponents.forEach((selection) => {
          if (!selection.variant) return;
          const row = document.createElement('div');
          row.className = 'lt-at600__summary-row lt-at600__summary-row--component';

          const label = document.createElement('span');
          label.textContent = `${selection.property}: ${selection.value}`;

          const price = document.createElement('span');
          price.textContent = `+${this.formatMoney(selection.variant.price)}`;

          row.append(label, price);
          this.componentsElement.append(row);
        });
      }

      if (missingVariant) {
        this.showError(`Pricing is not configured for ${missingVariant.value}.`);
      } else if (unavailableVariant) {
        this.showError(`${unavailableVariant.value} is currently unavailable.`);
      }

      this.submitButton.disabled = Boolean(missingVariant || unavailableVariant);
      this.updateStickyBar(selections, total, Boolean(missingVariant || unavailableVariant));
      this.updateMobilePrice(total);
    }

    updateStickyBar(selections, total, blocked) {
      if (!this.stickyBar) return;

      if (this.stickyTotal) this.stickyTotal.textContent = this.formatMoney(total);

      if (this.stickyProgress) {
        const chosen = selections.filter((selection) => selection.value).length;
        this.stickyProgress.textContent = chosen === this.selects.length
          ? `All ${this.selects.length} chosen`
          : `${chosen} of ${this.selects.length} chosen`;
      }

      if (this.stickyAddons) {
        const addOns = total - Number(this.baseVariant.price);
        this.stickyAddons.hidden = addOns <= 0;
        if (addOns > 0) this.stickyAddons.textContent = `+${this.formatMoney(addOns)} in add-ons`;
      }

      if (this.stickySubmit) this.stickySubmit.disabled = blocked;
    }

    async addConfiguration() {
      const firstInvalid = this.selects.find((select) => !select.value);
      if (firstInvalid) {
        if (firstInvalid.customDropdown) {
          firstInvalid.customDropdown.trigger.focus();
        } else {
          firstInvalid.reportValidity();
          firstInvalid.focus();
        }
        this.showError('Please choose an option for every required field.');
        return;
      }

      const selections = this.selects.map((select) => this.getSelection(select));
      const invalidComponent = selections.find((selection) => selection.sku && (!selection.variant || !selection.variant.available));
      if (invalidComponent) {
        this.showError(`${invalidComponent.value} cannot be added right now.`);
        return;
      }

      const quantityInput = document.getElementById(`Quantity-${this.dataset.sectionId}`);
      const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10));
      const configurationId = this.makeConfigurationId();
      const configurationProperties = Object.fromEntries(
        selections.map((selection) => [selection.property, selection.value])
      );
      configurationProperties['_AT600 Configuration ID'] = configurationId;

      const items = [{
        id: this.baseVariant.id,
        quantity,
        properties: configurationProperties,
      }];

      selections.forEach((selection) => {
        if (!selection.variant) return;
        items.push({
          id: selection.variant.id,
          quantity,
          properties: {
            '_AT600 Configuration ID': configurationId,
            '_AT600 Component': selection.property,
            '_AT600 Selection': selection.value,
          },
        });
      });

      const cart = document.querySelector('cart-drawer');
      const payload = { items };
      if (cart?.getSectionsToRender) {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
        cart.setActiveElement?.(document.activeElement);
      }

      this.setLoading(true);
      this.clearError();

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Unable to add this configuration to the cart.');
        }

        if (cart && result.sections) {
          cart.renderContents(result);
        } else {
          window.location.assign(window.routes?.cart_url || '/cart');
        }
      } catch (error) {
        this.showError(error.message || 'Unable to add this configuration to the cart.');
      } finally {
        this.setLoading(false);
      }
    }

    makeConfigurationId() {
      const random = globalThis.crypto?.getRandomValues
        ? globalThis.crypto.getRandomValues(new Uint32Array(1))[0].toString(36)
        : Math.random().toString(36).slice(2, 9);
      return `AT600-${Date.now().toString(36)}-${random}`.toUpperCase();
    }

    formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || 'USD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(Number(cents) / 100);
    }

    updateMobilePrice(cents) {
      const mobilePrice = document.querySelector('.lt-mobile-purchase-bar__price');
      if (mobilePrice) mobilePrice.textContent = this.formatMoney(cents);
    }

    setLoading(loading) {
      this.submitButton.disabled = loading;
      this.submitButton.setAttribute('aria-disabled', String(loading));
      this.submitButton.classList.toggle('loading', loading);
      this.submitButton.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !loading);
      if (this.stickySubmit) {
        this.stickySubmit.disabled = loading;
        this.stickySubmit.classList.toggle('loading', loading);
      }
    }

    showError(message) {
      this.errorMessage.textContent = message;
      this.errorElement.hidden = false;
    }

    clearError() {
      this.errorMessage.textContent = '';
      this.errorElement.hidden = true;
    }
  });
}
